import { Google } from "@mui/icons-material";
import ContactsIcon from "@mui/icons-material/Contacts";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  Card,
  Checkbox,
  MenuItem,
  Pagination,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { GridPaginationModel } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CustomSelect from "../../../../../components/ui/Select";
import apiService from "../../../../../service/Api";
import type { RootState } from "../../../../../store/Index";
import { tokens } from "../../../../../theme";
import type { IComment } from "../../../../../types/comment";
import { downloadApprovedComments } from "../utils/downloadApprovedComments";
import { handleBulkAction } from "../utils/handleBulkAction";
import { BulkConvertContactsModal } from "./BulkConvertContactsModal";
import { ImportCommentsModal } from "./ImportCommentsModal";
import { SyncCommentsModal } from "./SyncCommentsModal";
import { CommentMobileCard } from "./CommentsMobileCard";
import { CommentDesktopCard } from "./CommentDesktopCard";

interface MemorialMessage {
  id: number;
  stream: number;
  sender_full_name: string | null;
  sender_email: string | null;
  sender_phone_number: string | null;
  is_approved: boolean;
  approved_by: number | null;
  approved_by_username?: string | null;
  parent: number | null;
  content: string;
  created_at: string;
  updated_at: string;
}

interface CommentsTableProps {
  commentsType: string;
  endpoint: string;
  fetchApprovedComments: () => Promise<void>;
  resolvedStreamId?: number;
  nameOfDeceased?: string;
}

const CommentsTable: React.FC<CommentsTableProps> = ({
  commentsType,
  endpoint,
  fetchApprovedComments,
  resolvedStreamId,
  nameOfDeceased,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, reduxTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const bulkSelectAction = useRef<string>("approve");
  const { id } = useParams<{ id: string }>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [importCommentsModalOpen, setImportCommentsModalOpen] =
    useState<boolean>(false);
  const [bulkConvertModalOpen, setBulkConvertModalOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchedComments, setFetchedComments] = useState<IComment[]>([]);
  const [renderingComments, setRenderingComments] = useState<IComment[]>([]);
  const [checkedCommentsID, setCheckedCommentsID] = useState<string[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [existingContactEmails, setExistingContactEmails] = useState<string[]>(
    [],
  );

  const sourceLabel =
    commentsType === "youtube comments" ? "Youtube" : "Memorial Page";

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  useEffect(() => {
    const emails = fetchedComments
      .map((c) => c.authorEmail)
      .filter((e): e is string => Boolean(e) && e !== "Anonymous");

    if (emails.length === 0) return;

    apiService
      .post<{ existing_emails: string[] }>(
        "/api/contacts/contact/check-existing/",
        { emails },
      )
      .then((res) => {
        setExistingContactEmails(
          res.data.existing_emails.map((e) => e.toLowerCase()),
        );
      })
      .catch(() => {});
  }, [fetchedComments]);

  const fetchComments = useCallback(
    async (initialFetch: boolean) => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const params = {
          page: paginationModel.page + 1,
          page_size: paginationModel.pageSize,
          ordering: "-id",
        };
        const response = await apiService.get<{
          results: MemorialMessage[];
          count: number;
        }>(endpoint, params);

        const comments = response.data.results || [];

        const mappedComments: IComment[] = comments.map((comment) => ({
          id: comment.id.toString(),
          author: comment.sender_full_name || "Anonymous",
          authorEmail: comment.sender_email || "Anonymous",
          authorPhone: comment.sender_phone_number || null,
          streamId: comment.stream ?? null,
          avatar: "/api/placeholder/32/32",
          content: comment.content,
          timestamp: new Date(comment.created_at).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
          source: comment.is_approved ? "Manual Approval" : undefined,
          isApproved: comment.is_approved,
          approvedByUsername: comment.approved_by_username,
        }));

        setRowCount(response.data.count);
        setFetchedComments(mappedComments);
        if (initialFetch) {
          setRenderingComments(mappedComments);
        } else {
          setRenderingComments(mappedComments);
          await fetchApprovedComments();
        }
      } catch (err: unknown) {
        console.error("Failed to fetch comments:", err);
        toast.error("Failed to load comments. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      endpoint,
      fetchApprovedComments,
      id,
      paginationModel.page,
      paginationModel.pageSize,
    ],
  );

  useEffect(() => {
    fetchComments(true);
  }, [fetchComments]);

  const toggleChecked = () => {
    if (checkIsChecked()) {
      setCheckedCommentsID([]);
    } else {
      const commentsID: string[] = renderingComments.map(
        (comment: IComment) => comment.id,
      );
      setCheckedCommentsID([...commentsID]);
    }
  };

  const toggleSingleChecked = (commentId: string) => {
    setCheckedCommentsID((prev) =>
      prev.includes(commentId)
        ? prev.filter((cid) => cid !== commentId)
        : [...prev, commentId],
    );
  };

  const updateBulkAction = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } }),
  ) => {
    bulkSelectAction.current = String(e.target.value);
  };

  const checkIsChecked = () => {
    const renderingLength = renderingComments.length;
    const checkedCommentsLength = checkedCommentsID.length;
    if (renderingLength === 0 && checkedCommentsLength === 0) return false;
    if (renderingLength === checkedCommentsLength) return true;
    return false;
  };

  const refreshComments = (comment: IComment, isDeleting: boolean) => {
    if (isDeleting) {
      setFetchedComments((prev) => prev.filter((c) => c.id !== comment.id));
      setRenderingComments((prev) => prev.filter((c) => c.id !== comment.id));
      return;
    }
    setFetchedComments((prev) => [
      comment,
      ...prev.filter((c) => c.id !== comment.id),
    ]);
    setRenderingComments((prev) => [
      comment,
      ...prev.filter((c) => c.id !== comment.id),
    ]);
  };

  const filterComments = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } }),
  ) => {
    const value = e.target.value;
    switch (value) {
      case "all":
        setRenderingComments(fetchedComments);
        break;
      case "approved":
        setRenderingComments(fetchedComments.filter((c) => c.isApproved));
        break;
      case "pending":
        setRenderingComments(fetchedComments.filter((c) => !c.isApproved));
        break;
      default:
        setRenderingComments(fetchedComments);
    }
  };

  const checkIfCommentsApproved = () => {
    if (fetchedComments.length === 0) {
      return { bgColor: colors.primary[300], readyToDownload: false };
    }
    const approvedCommentAvailable = fetchedComments.some(
      (comment) => comment.isApproved === true,
    );
    if (approvedCommentAvailable) {
      return { bgColor: colors.primary[400], readyToDownload: true };
    }
  };

  const syncComments = () => setModalOpen(true);

  const toolbar = (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", lg: "center" },
        gap: 2,
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ textTransform: "capitalize" }}
        >
          {commentsType}
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-start" }}>
          <CustomSelect
            onChange={(e) => filterComments(e)}
            label=""
            defaultValue="all"
            sx={{ width: "120px" }}
            size="small"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
          </CustomSelect>

          <Box display="flex" gap={1} alignItems="center">
            <CustomSelect
              onChange={(e) => updateBulkAction(e)}
              label="Bulk Actions"
              defaultValue="approve"
              sx={{ width: "130px" }}
              size="small"
            >
              <MenuItem value="approve">Approve</MenuItem>
              <MenuItem value="spam">Spam</MenuItem>
              <MenuItem value="unapprove">Pending</MenuItem>
            </CustomSelect>
            <Button
              variant="contained"
              onClick={() =>
                handleBulkAction(
                  bulkSelectAction,
                  checkedCommentsID,
                  renderingComments,
                  setRenderingComments,
                  fetchComments,
                )
              }
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Box>

      {commentsType === "tribute comments" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "auto auto auto auto" },
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            disabled={checkedCommentsID.length === 0}
            onClick={() => setBulkConvertModalOpen(true)}
            sx={{ minWidth: 0, px: { xs: 1, sm: 2 } }}
          >
            <ContactsIcon sx={{ mr: 0.5 }} />
            Convert
          </Button>
          <Button
            variant="outlined"
            disabled
            sx={{ minWidth: 0, px: { xs: 1, sm: 2 } }}
          >
            <Google sx={{ mr: 0.5 }} />
            Sheets
          </Button>
          <Button
            variant="outlined"
            onClick={() => setImportCommentsModalOpen(true)}
            sx={{ minWidth: 0, px: { xs: 1, sm: 2 } }}
          >
            <FileUploadIcon sx={{ mr: 0.5 }} />
            Import
          </Button>
          <Button
            variant="contained"
            sx={{
              bgcolor: checkIfCommentsApproved()?.bgColor,
              minWidth: 0,
              px: { xs: 1, sm: 2 },
            }}
            disabled={!checkIfCommentsApproved()?.readyToDownload}
            onClick={() => downloadApprovedComments(id, nameOfDeceased || "")}
          >
            <DownloadIcon sx={{ mr: 0.5 }} />
            Approved
          </Button>
        </Box>
      )}

      {commentsType === "youtube comments" && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "auto auto" },
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            disabled={checkedCommentsID.length === 0}
            onClick={() => setBulkConvertModalOpen(true)}
          >
            <ContactsIcon sx={{ mr: 0.5 }} />
            Convert
          </Button>
          <Button variant="contained" onClick={syncComments}>
            <SyncIcon sx={{ mr: 0.5 }} />
            Sync
          </Button>
        </Box>
      )}
    </Box>
  );


  const mobileList = (
    <Box sx={{ mt: 3 }}>
      {commentsType === "youtube comments" && (
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2, ml: 0.5 }}>
          Last synced on 12/12/2025
        </Typography>
      )}

      {isLoading ? (
        [...Array(4)].map((_, i) => (
          <Card
            key={i}
            variant="outlined"
            sx={{ mb: 1.5, borderRadius: 2, p: 2 }}
          >
            <Skeleton variant="text" width="50%" height={24} />
            <Skeleton variant="text" width="35%" height={18} sx={{ mb: 1 }} />
            <Skeleton
              variant="rectangular"
              height={56}
              sx={{ borderRadius: 1, mb: 1 }}
            />
            <Skeleton
              variant="rectangular"
              height={36}
              sx={{ borderRadius: 1 }}
            />
          </Card>
        ))
      ) : renderingComments.length === 0 ? (
        <Typography sx={{ textAlign: "center", py: 4, opacity: 0.6 }}>
          No comments found.
        </Typography>
      ) : (
        renderingComments.map((comment, index) => (
          <CommentMobileCard
            key={comment.id}
            comment={comment}
            refreshComments={refreshComments}
            fetchApprovedComments={fetchApprovedComments}
            existingContactEmails={existingContactEmails}
            isLast={index === renderingComments.length - 1}
          />
        ))
      )}

      {!isLoading && renderingComments.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 2 }}>
          <Pagination
            count={Math.max(1, Math.ceil(rowCount / paginationModel.pageSize))}
            page={paginationModel.page + 1}
            onChange={(_, page) =>
              handlePaginationModelChange({
                ...paginationModel,
                page: page - 1,
              })
            }
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );

  const desktopList = (
    <Box sx={{ mt: 3 }}>
      {commentsType === "youtube comments" && (
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 2, ml: 1 }}>
          Last synced on 12/12/2025
        </Typography>
      )}

      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          p: { xs: 2, sm: 3 },
          bgcolor: colors.primary[900],
        }}
      >
        {!isLoading && renderingComments.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pb: 1,
              mb: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Checkbox checked={checkIsChecked()} onChange={toggleChecked} />
            <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.7 }}>
              Select all
            </Typography>
          </Box>
        )}

        {isLoading ? (
          [...Array(4)].map((_, i) => (
            <Box key={i} sx={{ py: 1.5 }}>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Skeleton variant="circular" width={20} height={20} />
                <Skeleton variant="circular" width={36} height={36} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="30%" height={18} />
                  <Skeleton variant="text" width="20%" height={14} sx={{ mt: 0.5 }} />
                  <Skeleton variant="text" width="100%" height={14} sx={{ mt: 1.5 }} />
                  <Skeleton variant="text" width="90%" height={14} />
                  <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
                    <Skeleton variant="rounded" width={70} height={26} />
                    <Skeleton variant="rounded" width={60} height={26} />
                    <Skeleton variant="rounded" width={50} height={26} />
                    <Skeleton variant="rounded" width={50} height={26} />
                    <Skeleton variant="rounded" width={70} height={26} />
                  </Box>
                </Box>
              </Box>
            </Box>
          ))
        ) : renderingComments.length === 0 ? (
          <Typography sx={{ textAlign: "center", py: 4, opacity: 0.6 }}>
            No comments found.
          </Typography>
        ) : (
          renderingComments.map((comment, index) => (
            <CommentDesktopCard
              key={comment.id}
              comment={comment}
              checked={checkedCommentsID.includes(comment.id)}
              onCheck={() => toggleSingleChecked(comment.id)}
              isLast={index === renderingComments.length - 1}
              refreshComments={refreshComments}
              fetchApprovedComments={fetchApprovedComments}
              existingContactEmails={existingContactEmails}
              sourceLabel={sourceLabel}
            />
          ))
        )}
      </Card>

      {!isLoading && renderingComments.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "right", pt: 2, pb: 1, mr: 12 }}>
          <Pagination
            count={Math.max(1, Math.ceil(rowCount / paginationModel.pageSize))}
            page={paginationModel.page + 1}
            onChange={(_, page) =>
              handlePaginationModelChange({
                ...paginationModel,
                page: page - 1,
              })
            }
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {resolvedStreamId !== undefined && (
        <SyncCommentsModal
          id={resolvedStreamId}
          open={modalOpen}
          setOpen={setModalOpen}
          onSynced={() => {
            fetchComments(false);
            fetchApprovedComments();
          }}
        />
      )}

      {importCommentsModalOpen && (
        <ImportCommentsModal
          setOpen={setImportCommentsModalOpen}
          fetchComments={fetchComments}
        />
      )}

      {bulkConvertModalOpen && (
        <BulkConvertContactsModal
          open={bulkConvertModalOpen}
          setOpen={setBulkConvertModalOpen}
          comments={renderingComments.filter((c) =>
            checkedCommentsID.includes(c.id),
          )}
        />
      )}

      {toolbar}
      {isMobile ? mobileList : desktopList}
    </>
  );
};

CommentsTable.propTypes = {
  commentsType: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  fetchApprovedComments: PropTypes.func.isRequired,
};

export default CommentsTable;